import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSave, faTrash, faLink, faPlus } from '@fortawesome/free-solid-svg-icons';
import config from '../../configs/config';
import { useEscapeKey } from '../hooks/useEscapeKey';
import './styles/RedirectModal.css';

// Each redirect is stored (and sent to the survey app) as a single URL
// TEMPLATE string, e.g. "https://x.com/thanks?id={uid}&status={status}".
// {uid}/{pid}/{status} are built-in tokens the survey app substitutes at
// runtime; any other {Token} is looked up from that respondent's own
// answers (covers pass-in variables like {AUD}). This modal is just a
// friendlier editor on top of that same template string - it never
// changes the underlying format, so nothing downstream (PollStudioNST)
// needs to know a structured builder was used to produce it.
const SOURCE_OPTIONS = [
  { value: 'uid', label: 'Respondent UID' },
  { value: 'pid', label: 'Survey/Project ID' },
  { value: 'status', label: 'Outcome status' },
  { value: 'answer', label: 'Answer / question code…' },
  { value: 'static', label: 'Fixed text…' },
];

const EMPTY_FIELD = { baseUrl: '', params: [] };
const EMPTY_FIELDS = { complete: EMPTY_FIELD, screenout: EMPTY_FIELD, quotafull: EMPTY_FIELD, terminate: EMPTY_FIELD };

const FIELDS_META = [
  { key: 'complete', label: 'Completion URL', hint: 'Respondent is sent here after successfully completing the survey.' },
  { key: 'screenout', label: 'Screenout URL', hint: 'Respondent is sent here when a screenout answer disqualifies them.' },
  { key: 'quotafull', label: 'Quota Full URL', hint: 'Respondent is sent here when the quota cell they belong to is full.' },
  { key: 'terminate', label: 'Terminate URL', hint: 'Respondent is sent here for any other termination not covered above.' },
];

let paramIdCounter = 0;
const nextParamId = () => `p${++paramIdCounter}`;

const tokenForSource = (source, varName) =>
  source === 'answer' ? `{${(varName || '').trim()}}` : `{${source}}`;

// Assemble the final template string from a base URL + parameter rows.
// Tokens ({uid}, {pid}, {status}, {AnyKey}) are left as literal braces -
// they must NOT be URL-encoded here, or the survey app's substitution
// regex won't recognize them anymore. Only static (fixed-text) values are
// encoded, since those are already final.
const buildUrl = (baseUrl, params) => {
  const base = (baseUrl || '').trim();
  if (!base) return '';
  const query = (params || [])
    .filter(p => p.key && p.key.trim())
    .map(p => {
      const value = p.source === 'static'
        ? encodeURIComponent(p.staticValue || '')
        : tokenForSource(p.source, p.varName);
      return `${encodeURIComponent(p.key.trim())}=${value}`;
    })
    .join('&');
  if (!query) return base;
  return `${base}${base.includes('?') ? '&' : '?'}${query}`;
};

// Reverse of buildUrl(), for loading a previously-saved template back into
// the structured editor. Any query value shaped like {token} is recognized
// as a dynamic source; everything else is treated as a fixed value.
const parseUrl = (template) => {
  if (!template) return { baseUrl: '', params: [] };
  const qIdx = template.indexOf('?');
  if (qIdx === -1) return { baseUrl: template, params: [] };
  const baseUrl = template.slice(0, qIdx);
  const params = template.slice(qIdx + 1).split('&').filter(Boolean).map(pair => {
    const eqIdx = pair.indexOf('=');
    const rawKey = eqIdx === -1 ? pair : pair.slice(0, eqIdx);
    const rawValue = eqIdx === -1 ? '' : pair.slice(eqIdx + 1);
    let key = rawKey;
    try { key = decodeURIComponent(rawKey); } catch { /* leave as-is */ }

    const tokenMatch = rawValue.match(/^\{([^{}]+)\}$/);
    if (tokenMatch) {
      const token = tokenMatch[1];
      if (token === 'uid' || token === 'pid' || token === 'status') {
        return { id: nextParamId(), key, source: token };
      }
      return { id: nextParamId(), key, source: 'answer', varName: token };
    }
    let staticValue = rawValue;
    try { staticValue = decodeURIComponent(rawValue); } catch { /* leave as-is */ }
    return { id: nextParamId(), key, source: 'static', staticValue };
  });
  return { baseUrl, params };
};

const RedirectModal = ({ isOpen, onClose, projectId, onSave }) => {
  const [fields, setFields] = useState(EMPTY_FIELDS);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // ─── Load Redirects (PostgreSQL) ───
  useEffect(() => {
    if (!isOpen || !projectId) return;

    const loadRedirects = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${config.API_BASE_URL}/get-redirect-config/${projectId}`, {
          credentials: 'include',
        });
        const result = await res.json();

        if (result.success && result.redirects) {
          setFields({
            complete: parseUrl(result.redirects.complete),
            screenout: parseUrl(result.redirects.screenout),
            quotafull: parseUrl(result.redirects.quotafull),
            terminate: parseUrl(result.redirects.terminate),
          });
        } else {
          setFields(EMPTY_FIELDS);
        }
      } catch (err) {
        console.error('Failed to load redirects:', err);
      } finally {
        setLoading(false);
      }
    };

    loadRedirects();
  }, [isOpen, projectId]);

  const updateField = (key, updater) => {
    setFields(prev => ({ ...prev, [key]: updater(prev[key]) }));
  };

  const setBaseUrl = (key, baseUrl) => updateField(key, f => ({ ...f, baseUrl }));

  const addParam = (key) => updateField(key, f => ({
    ...f,
    params: [...f.params, { id: nextParamId(), key: '', source: 'uid' }],
  }));

  const removeParam = (key, paramId) => updateField(key, f => ({
    ...f,
    params: f.params.filter(p => p.id !== paramId),
  }));

  const updateParam = (key, paramId, patch) => updateField(key, f => ({
    ...f,
    params: f.params.map(p => (p.id === paramId ? { ...p, ...patch } : p)),
  }));

  // ─── Save Redirects (dedicated endpoint - preserves quotas) ───
  const handleSave = async () => {
    const built = {
      complete: buildUrl(fields.complete.baseUrl, fields.complete.params),
      screenout: buildUrl(fields.screenout.baseUrl, fields.screenout.params),
      quotafull: buildUrl(fields.quotafull.baseUrl, fields.quotafull.params),
      terminate: buildUrl(fields.terminate.baseUrl, fields.terminate.params),
    };

    if (Object.values(built).every(u => !u)) {
      if (!window.confirm('All redirect URLs are empty. Continue?')) {
        return;
      }
    }

    setSaving(true);
    try {
      const saveRes = await fetch(`${config.API_BASE_URL}/save-redirect-config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ projectId, redirects: built }),
      });

      const saveResult = await saveRes.json();

      if (saveResult.success) {
        onSave?.(built);
        onClose();
      } else {
        alert('Failed to save redirects: ' + (saveResult.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error saving redirects:', err);
      alert('Error saving redirects');
    } finally {
      setSaving(false);
    }
  };

  // ─── Clear all redirects ───
  const handleClear = () => {
    if (window.confirm('Clear all redirect URLs?')) {
      setFields(EMPTY_FIELDS);
    }
  };

  useEscapeKey(isOpen, onClose);

  if (!isOpen) return null;

  return (
    <div className="redirect-modal-overlay" onClick={onClose}>
      <div className="redirect-modal-content" onClick={e => e.stopPropagation()}>
        <div className="redirect-modal-header">
          <div className="redirect-header-left">
            <h3>
              <FontAwesomeIcon icon={faLink} style={{ marginRight: "8px" }} />
              Redirect URLs
            </h3>
          </div>
          <div className="redirect-header-right">
            <button className="redirect-close-btn" onClick={onClose}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>

        <div className="redirect-modal-body">
          {loading ? (
            <p className="redirect-loading">Loading redirects...</p>
          ) : (
            <div className="redirect-fields">
              <p className="redirect-token-help">
                Any redirect left blank falls back to this survey's default page for that outcome
                (Thank You / Quota Full / Screenout / Terminate) — a redirect URL is optional.
                Add parameters below to build the final link automatically; each one can pull a
                live value (respondent UID, the outcome status, or any of this survey's answers)
                or use fixed text.
              </p>

              {FIELDS_META.map(meta => {
                const field = fields[meta.key];
                const preview = buildUrl(field.baseUrl, field.params);
                return (
                  <div className="redirect-field-block" key={meta.key}>
                    <label>{meta.label}</label>
                    <small className="redirect-hint">{meta.hint}</small>

                    <input
                      type="text"
                      className="redirect-base-url-input"
                      value={field.baseUrl}
                      onChange={e => setBaseUrl(meta.key, e.target.value)}
                      placeholder="https://example.com/thankyou.htm  (leave blank to use the default page)"
                    />

                    {field.params.length > 0 && (
                      <div className="redirect-params-list">
                        {field.params.map(p => (
                          <div className="redirect-param-row" key={p.id}>
                            <input
                              type="text"
                              className="redirect-param-key"
                              placeholder="param name (e.g. id)"
                              value={p.key}
                              onChange={e => updateParam(meta.key, p.id, { key: e.target.value })}
                            />
                            <span className="redirect-param-eq">=</span>
                            <select
                              className="redirect-param-source"
                              value={p.source}
                              onChange={e => updateParam(meta.key, p.id, { source: e.target.value })}
                            >
                              {SOURCE_OPTIONS.map(o => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                              ))}
                            </select>
                            {p.source === 'answer' && (
                              <input
                                type="text"
                                className="redirect-param-extra"
                                placeholder="question code (e.g. AUD)"
                                value={p.varName || ''}
                                onChange={e => updateParam(meta.key, p.id, { varName: e.target.value })}
                              />
                            )}
                            {p.source === 'static' && (
                              <input
                                type="text"
                                className="redirect-param-extra"
                                placeholder="fixed value"
                                value={p.staticValue || ''}
                                onChange={e => updateParam(meta.key, p.id, { staticValue: e.target.value })}
                              />
                            )}
                            <button
                              type="button"
                              className="redirect-param-remove"
                              onClick={() => removeParam(meta.key, p.id)}
                              aria-label="Remove parameter"
                              title="Remove parameter"
                            >
                              <FontAwesomeIcon icon={faTimes} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    <button
                      type="button"
                      className="redirect-add-param-btn"
                      onClick={() => addParam(meta.key)}
                      disabled={!field.baseUrl}
                    >
                      <FontAwesomeIcon icon={faPlus} /> Add parameter
                    </button>

                    {preview && (
                      <div className="redirect-preview">
                        <span className="redirect-preview-label">Preview:</span>
                        <code className="redirect-preview-url">{preview}</code>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="redirect-modal-footer">
          <button
            className="redirect-clear-btn"
            onClick={handleClear}
            disabled={loading || saving}
          >
            <FontAwesomeIcon icon={faTrash} /> Clear All
          </button>
          <button
            className="redirect-cancel-btn"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            className="redirect-save-btn"
            onClick={handleSave}
            disabled={saving || loading}
          >
            {saving ? (
              'Saving...'
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} /> Save Redirects
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RedirectModal;
